import React, { useMemo, useState } from 'react';
import { Grid, Button, TextField, Autocomplete } from '@mui/material';
import {
	createPosition,
	JobCode,
	SchedulerPosition,
} from '../../../../core/API/scheduler';
import styles from '../SchedulerSetting.module.scss';

interface CreatePositionProps {
	backButtonClick: () => void;
	onPositionCreated?: () => void;
	jobCodes: JobCode[];
	existingPositions: SchedulerPosition[];
}

const CreatePosition: React.FC<CreatePositionProps> = ({
	backButtonClick,
	onPositionCreated,
	jobCodes,
	existingPositions,
}) => {
	const [name, setName] = useState('');
	const [selectedJobCode, setSelectedJobCode] = useState<JobCode | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const sortedJobCodes = useMemo(
		() =>
			[...jobCodes].sort((a, b) => {
				const aNum = a.jobNumber ?? 999;
				const bNum = b.jobNumber ?? 999;
				return aNum - bNum;
			}),
		[jobCodes]
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!name.trim()) {
			setError('Position name is required');
			return;
		}

		const duplicate = existingPositions.some(
			(p) => p.name.toLowerCase() === name.trim().toLowerCase()
		);
		if (duplicate) {
			setError('Position name must be unique');
			return;
		}

		if (!selectedJobCode) {
			setError('Job code is required');
			return;
		}

		setIsSubmitting(true);
		setError(null);

		try {
			await createPosition({
				name: name.trim(),
				jobCode: selectedJobCode['@id'],
			});
			onPositionCreated?.();
			setTimeout(() => {
				backButtonClick();
			}, 1500);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.['hydra:description'] ||
				err?.response?.data?.message ||
				(err instanceof Error ? err.message : 'Failed to create position');
			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	return (
		<div className={styles.createJobCode}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Button className={styles.backButton} onClick={backButtonClick}>
						Back
					</Button>
					<Button className={styles.submitButton} onClick={handleSubmit} disabled={isSubmitting}>
						{isSubmitting ? 'Saving...' : 'Save'}
					</Button>
				</Grid>
				<Grid>
					<h1 className={styles.formTitle}>Create New Position</h1>
				</Grid>
			</Grid>

			<form className={styles.formContainer} onSubmit={handleSubmit}>
				{error && <div style={{ color: '#d32f2f', marginBottom: 16 }}>{error}</div>}

				<TextField
					label="Position Name"
					name="name"
					placeholder="e.g. Station 17"
					value={name}
					onChange={(e) => setName(e.target.value)}
					className={styles.input}
					margin="normal"
					fullWidth
					required
				/>

				<Autocomplete
					options={sortedJobCodes}
					getOptionLabel={(option) =>
						option.jobNumber
							? `${String(option.jobNumber).padStart(2, '0')} - ${option.label}`
							: option.label
					}
					value={selectedJobCode}
					onChange={(_, value) => setSelectedJobCode(value)}
					isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Job Code"
							margin="normal"
							className={styles.input}
							required
						/>
					)}
				/>
			</form>
		</div>
	);
};

export default CreatePosition;
